// Spec 38's pure helpers: no Vue, like fleetFormat.js beside it. Everything a
// PM status chip, a work-order row, a hold banner or an inspection report line
// prints comes through here, so the truck's Maintenance page, the fleet board
// and the work order dialog say the same thing about the same record.
//
// WORDING RULE (spec 38 §1.6, and the review of 2026-10-11). The inspection
// report (DVIR) is the ONE place in the product where regulatory wording
// belongs: "Driver vehicle inspection report", "repair certification". It
// never leaks onto the per-stop freight checklist, which is not an
// inspection. "Out of service" is NOT ours to say: it is a roadside order
// under 49 CFR 396.9(c). The company's own hold reads "Held: not available"
// (the API's field names, routes and codes keep out_of_service). And nothing
// here says a record "satisfies" or "complies with" a rule: the product says
// what was recorded, by whom and when.

/**
 * The status chip for a PM schedule (pmStatus.statusOf's `status`): { value,
 * label } for LwChip's feedback axis. `unknown` is a distance-only schedule
 * with no odometer to measure against, and says so rather than reading as
 * fine: spec 38 §7, "unknown, never ok".
 */
export function pmStatusChip(schedule) {
  const s = (schedule && schedule.status) || 'unknown';
  return (
    {
      overdue: { value: 'danger', label: 'Overdue' },
      due_soon: { value: 'warning', label: 'Due soon' },
      ok: { value: 'success', label: 'OK' },
    }[s] || { value: 'neutral', label: 'Not enough to measure' }
  );
}

/** 125000 -> "125,000 mi"; one decimal at most, never a trailing ".0". */
export function formatDistance(value, unit) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const text = n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return unit ? `${text} ${unit}` : text;
}

function plural(n, word) {
  const abs = Math.abs(n);
  return `${abs} ${word}${abs === 1 ? '' : 's'}`;
}

/** "Every 15,000 mi or 90 days" / "Every 90 days" / "Every 15,000 mi". */
export function intervalText(schedule) {
  const s = schedule || {};
  const parts = [];
  if (s.interval_distance) parts.push(formatDistance(s.interval_distance, s.distance_unit || 'mi'));
  if (s.interval_days) parts.push(plural(Number(s.interval_days), 'day'));
  return parts.length ? `Every ${parts.join(' or ')}` : '';
}

/**
 * When the schedule is next due, by date and by distance, each as a short
 * sentence (both when the schedule has both). A schedule the server could not
 * measure says what it is missing.
 */
export function pmDueLines(schedule) {
  const s = schedule || {};
  const lines = [];
  if (s.due_on) {
    const d = Number(s.days_left);
    if (d < 0) lines.push(`Was due ${s.due_on} (${plural(d, 'day')} ago)`);
    else if (d === 0) lines.push(`Due today (${s.due_on})`);
    else lines.push(`Due ${s.due_on} (in ${plural(d, 'day')})`);
  }
  if (s.due_at != null) {
    const unit = s.unit || s.distance_unit || 'mi';
    if (s.distance_left == null) {
      lines.push(`Due at ${formatDistance(s.due_at, unit)}`);
    } else if (Number(s.distance_left) <= 0) {
      lines.push(
        `Due at ${formatDistance(s.due_at, unit)} (${formatDistance(-Number(s.distance_left), unit)} past)`
      );
    } else {
      lines.push(
        `Due at ${formatDistance(s.due_at, unit)} (${formatDistance(s.distance_left, unit)} left)`
      );
    }
  }
  if (!lines.length) {
    if (s.interval_distance && s.last_done_odometer == null) {
      lines.push('No odometer recorded for the last service');
    } else if (s.interval_distance && !s.odometer) {
      lines.push('No odometer reading for this unit yet');
    } else if (s.interval_days && !s.last_done_on) {
      lines.push('No date recorded for the last service');
    }
  }
  return lines;
}

/** "Last done 2026-08-01 at 110,000 mi" / "Never recorded". */
export function lastDoneText(schedule) {
  const s = schedule || {};
  const bits = [];
  if (s.last_done_on) bits.push(s.last_done_on);
  if (s.last_done_odometer != null) {
    bits.push(`at ${formatDistance(s.last_done_odometer, s.last_done_unit || s.distance_unit)}`);
  }
  return bits.length ? `Last done ${bits.join(' ')}` : 'Last service not recorded';
}

/**
 * Where the odometer figure came from (spec 38 §1.3's order): the engine
 * (telematics or the OBD adapter), a typed or adapter odometer reading, or a
 * trip reading.
 */
export function odometerSourceLabel(source) {
  if (source === 'telematics' || source === 'obd') return 'From the engine';
  if (source === 'reading') return 'From an odometer reading';
  if (source === 'trip') return 'From a trip reading';
  return 'Source not recorded';
}

/** Work-order status chip. */
export function workOrderStatusChip(status) {
  return (
    {
      open: { value: 'info', label: 'Open' },
      in_progress: { value: 'warning', label: 'In progress' },
      done: { value: 'success', label: 'Done' },
    }[status] || { value: 'neutral', label: status || 'Unknown' }
  );
}

/** Where a work order came from. */
export function openedFromLabel(openedFrom) {
  return (
    {
      pm: 'PM schedule',
      dvir_defect: 'Inspection report',
      fault_event: 'Vehicle fault',
      manual: 'Entered by the office',
    }[openedFrom] ||
    openedFrom ||
    ''
  );
}

/** The company's own hold, as the office reads it (never "out of service"). */
export const HOLD_LABEL = 'Held: not available';

/** Why a hold (an out_of_service period, in the API's words) is open: its source. */
export function outOfServiceSourceLabel(source) {
  return (
    {
      manual: 'Held by the office',
      dvir_defect: 'Inspection report defect',
      fault_event: 'Vehicle fault',
      work_order: 'Work order',
    }[source] ||
    source ||
    ''
  );
}

/** The defect id an inspection-report period waits on ('dvir_defect:41' -> 41). */
export function defectIdOfPeriod(period) {
  const ref = period && period.source_ref;
  const m = typeof ref === 'string' ? /^dvir_defect:(\d+)$/.exec(ref) : null;
  return m ? Number(m[1]) : null;
}

/** The work order id a work-order or fault period belongs to ('work_order:7' -> 7). */
export function workOrderIdOfPeriod(period) {
  const ref = period && period.source_ref;
  const m = typeof ref === 'string' ? /^work_order:(\d+)$/.exec(ref) : null;
  return m ? Number(m[1]) : null;
}

export const CERTIFICATIONS = Object.freeze([
  Object.freeze({ value: 'repaired', label: 'Repaired' }),
  Object.freeze({ value: 'repair_not_needed', label: 'Repair not needed' }),
]);

export function certificationLabel(value) {
  const found = CERTIFICATIONS.find(c => c.value === value);
  return found ? found.label : value || '';
}

export function dvirKindLabel(kind) {
  if (kind === 'pre_trip') return 'Pre-trip';
  if (kind === 'post_trip') return 'Post-trip';
  if (kind === 'ad_hoc') return 'Reported during the day';
  return kind || '';
}

/**
 * Who certifies a repair, as the office chooses it (DvirService.certifier):
 * the carrier's own office, or a named repair facility. A driver certifying
 * en route as the carrier's agent ('driver_agent') does so from the app, not
 * from here, so it is not offered.
 */
export const CERTIFIER_KINDS = Object.freeze([
  Object.freeze({ value: 'office', label: 'Our office' }),
  Object.freeze({ value: 'repair_facility', label: 'Repair facility' }),
]);

/** "office" / "driver as the carrier's agent" / "repair facility: Valley Truck Repair". */
export function certifierKindText(defect) {
  const d = defect || {};
  if (d.certifier_kind === 'office') return 'office';
  if (d.certifier_kind === 'driver_agent') return 'driver as the carrier’s agent';
  if (d.certifier_kind === 'repair_facility') {
    return d.certifier_org_name ? `repair facility: ${d.certifier_org_name}` : 'repair facility';
  }
  return d.certifier_kind || '';
}

/**
 * The carrier name the server puts in a certification statement
 * (DvirService.carrierName: the legal name, else the name). Null while the
 * organization has not arrived.
 */
export function carrierNameOf(store) {
  const org = store && store.state && store.state.organization;
  const name = org && (org.legal_name || org.name);
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/**
 * A PREVIEW of the fixed statement the server records with a certification
 * (api/app/Services/Readiness/dvirStatements.js, certification_repaired /
 * certification_not_needed). The server renders and stores its own copy;
 * this is so the person certifying reads it first. Without a carrier name
 * (the organization not loaded) the sentence leaves the name out rather than
 * guessing one. Empty until a choice is made.
 */
export function certificationStatement(certification, carrier) {
  const forWhom = carrier ? ` for ${carrier}` : '';
  if (certification === 'repaired')
    return `I certify${forWhom} that this defect has been repaired.`;
  if (certification === 'repair_not_needed') {
    return `I certify${forWhom} that repair of this defect is unnecessary.`;
  }
  return '';
}

/**
 * One line for a defect's certification state: "Repair not yet certified",
 * or "Repaired · certified by Dana Dispatch (repair facility: Valley Truck
 * Repair), Oct 10, 2026, 9:00 AM". The statement itself is shown on its own
 * line by the caller (certification_statement).
 */
export function certificationSummary(defect, zone) {
  const d = defect || {};
  if (d.status === 'open' || !d.certification) return 'Repair not yet certified';
  let line = certificationLabel(d.certification);
  const who = d.certified_by_name;
  const kind = certifierKindText(d);
  if (who || kind) {
    line += ` · certified by ${who || 'someone not recorded'}`;
    if (kind) line += ` (${kind})`;
  }
  if (d.certified_at) line += `, ${formatInstant(d.certified_at, zone)}`;
  return line;
}

/** "Told by Sam Driver" when someone else told the driver about the defect. */
export function reportedByText(defect) {
  const by = defect && defect.reported_by;
  return by ? `Told by ${by}` : '';
}

/**
 * The unit a report or a defect names: "Unit 214", or for a unit the phone
 * could not name, what the driver typed and that it is not matched.
 */
export function reportUnitText(row) {
  const r = row || {};
  if (r.truck_id == null && r.unit_label) {
    return `Typed as '${r.unit_label}' — not matched to a unit`;
  }
  if (r.unit_number) return `Unit ${r.unit_number}`;
  return r.unit_label ? `Typed as '${r.unit_label}' — not matched to a unit` : '';
}

/** The towed units on a report: "Trailer 5301, Trailer 5302" (any number). */
export function towedUnitsText(units) {
  return (units || [])
    .map(u => `${u.type === 'trailer' ? 'Trailer' : 'Unit'} ${u.unit_number}`)
    .join(', ');
}

/** Where a report's printable page opens (a new tab; print it or save it as a PDF). */
export function dvirPrintHref(dvirId) {
  return `/api/fleet/dvirs/${encodeURIComponent(dvirId)}/print`;
}

/** An electronic-report consent row, in a sentence's first half. */
export function consentText(row) {
  const r = row || {};
  const who = r.party === 'carrier' ? 'Carrier' : r.party === 'driver' ? 'Driver' : r.party || '';
  const did = r.action === 'withdrawn' ? 'withdrew consent' : 'consented';
  return `${who}${r.name ? ` (${r.name})` : ''} ${did}`.trim();
}

export const LINE_KINDS = Object.freeze([
  Object.freeze({ value: 'part', label: 'Part' }),
  Object.freeze({ value: 'labour', label: 'Labour' }),
  Object.freeze({ value: 'other', label: 'Other' }),
]);

/**
 * A line's amount for the dialog's PREVIEW only: quantity x unit cost to the
 * cent, the rule WorkOrderService.validateLines applies. The server computes
 * the stored amounts and the total and never takes them from here; this is
 * so the person typing sees the sum before they save. A blank quantity is 1
 * and a blank cost is 0, as the dialog sends them; null when either is not a
 * number at all.
 */
export function lineAmount(line) {
  const l = line || {};
  const qty = l.quantity == null || l.quantity === '' ? 1 : Number(l.quantity);
  const cost = l.unit_cost == null || l.unit_cost === '' ? 0 : Number(l.unit_cost);
  if (!Number.isFinite(qty) || !Number.isFinite(cost)) return null;
  return Math.round(qty * cost * 100) / 100;
}

export function linesTotal(lines) {
  return Math.round((lines || []).reduce((sum, l) => sum + (lineAmount(l) || 0), 0) * 100) / 100;
}

/** 1234.5 -> "$1,234.50" in the tenant's currency (org.currency, USD by default). */
export function formatMoney(amount, currency = 'USD') {
  if (amount == null || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return n.toLocaleString('en-US', { style: 'currency', currency: currency || 'USD' });
  } catch (e) {
    return `${n.toFixed(2)} ${currency}`;
  }
}

/**
 * An instant as the office reads it, in the org's zone: "Oct 11, 2026, 9:04 AM".
 * Empty for nothing; the raw string for something unparseable rather than
 * "Invalid Date".
 */
export function formatInstant(iso, zone) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleString('en-US', {
      timeZone: zone || undefined,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return d.toISOString();
  }
}

/** Where a work order's invoice and a defect's photo open (the API streams them; cookie session). */
export function invoiceHref(workOrderId) {
  return `/api/fleet/work-orders/${encodeURIComponent(workOrderId)}/invoice`;
}

export function defectPhotoHref(defectId) {
  return `/api/fleet/dvir-defects/${encodeURIComponent(defectId)}/photo`;
}

/** The sentence a refused maintenance write shows. */
export function maintenanceProblem(err, fallback) {
  const res = err && err.response;
  if (!res) return 'The request did not reach the server. Check your connection and try again.';
  if (res.status === 403) return 'This needs a dispatcher or administrator account.';
  if (res.status === 404) return 'That record is no longer here. Reload to see where it stands.';
  if (res.status === 409) {
    return 'Someone already dealt with this one. Reload to see where it stands.';
  }
  const errors = res.data && res.data.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors).find(v => Array.isArray(v) && v.length);
    if (first) return String(first[0]);
  }
  return fallback;
}
